// frontend/src/pages/InquiryListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Space, message, Typography, Popconfirm, Modal, Form, Select, Input, Descriptions, Spin, Drawer, Avatar, Tooltip, Card } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, UserOutlined, PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getInquiriesByStatus, acceptInquiry, rejectInquiry, getInquiryById } from '../services/inquiryService';
import { getAllClasses } from '../services/classService';
import { imageBaseUrl } from '../services/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import femaleAvatar from '../assets/female.png';
import maleAvatar from '../assets/male.png';
import schoolLogo from '../assets/logo.png';
import useResponsive from '../hooks/useResponsive';

const { Title, Text } = Typography;
const { Option } = Select;

const InquiryListPage = ({ status = 'pending' }) => {
    const { currentYear } = useAcademicYear();
    const { isMobile } = useResponsive();
    const [inquiries, setInquiries] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [isAcceptModalVisible, setIsAcceptModalVisible] = useState(false);
    const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [form] = Form.useForm();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [inquiryData, classData] = await Promise.all([getInquiriesByStatus(status), getAllClasses()]);
            setInquiries(inquiryData.map(i => ({ ...i, key: i.id })));
            setClasses(classData);
        } catch (error) {
            message.error(`Failed to load ${status} inquiries.`);
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        if (currentYear) {
            fetchData();
        }
    }, [currentYear, fetchData]);

    const handleAcceptClick = (inquiry) => {
        setSelectedInquiry(inquiry);
        setIsAcceptModalVisible(true);
        form.setFieldsValue({
            classId: inquiry.class_id || undefined,
            admissionNo: inquiry.admission_no || undefined
        });
    };

    const handleModalCancel = () => { setIsAcceptModalVisible(false); setSelectedInquiry(null); };

    const getAvatarSrc = (item) => {
        if (!item) return maleAvatar;
        return item.profile_picture_url
            ? `${imageBaseUrl}${item.profile_picture_url}`
            : (item.gender && item.gender.toLowerCase() === 'female' ? femaleAvatar : maleAvatar);
    };

    // Helper to load image
    const loadImage = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = url;
        });
    };

    // Helper to create rounded and filled image using canvas
    const getRoundedImage = (img, targetW_mm, targetH_mm, radius_mm) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 12 pixels per mm gives good print quality
        const pxPerMm = 12; 
        const w = targetW_mm * pxPerMm;
        const h = targetH_mm * pxPerMm;
        const r = radius_mm * pxPerMm;
        
        canvas.width = w;
        canvas.height = h;

        // Create rounded path
        ctx.beginPath();
        if (ctx.roundRect) {
            ctx.roundRect(0, 0, w, h, r);
        } else {
            // Fallback for older browsers
            ctx.moveTo(r, 0);
            ctx.arcTo(w, 0, w, h, r);
            ctx.arcTo(w, h, 0, h, r);
            ctx.arcTo(0, h, 0, 0, r);
            ctx.arcTo(0, 0, w, 0, r);
        }
        ctx.clip();
        
        // Center-cover filling logic
        const imgRatio = img.width / img.height;
        const targetRatio = w / h;
        let drawW, drawH, drawX, drawY;

        if (imgRatio > targetRatio) {
            drawH = h;
            drawW = h * imgRatio;
            drawX = (w - drawW) / 2;
            drawY = 0;
        } else {
            drawW = w;
            drawH = w / imgRatio;
            drawX = 0;
            drawY = (h - drawH) / 2;
        }

        ctx.drawImage(img, drawX, drawY, drawW, drawH);
        return canvas.toDataURL('image/png');
    };

    const handlePrintIDCard = async (inquiryOrList) => {
        const items = Array.isArray(inquiryOrList) ? inquiryOrList : [inquiryOrList];
        if (items.length === 0) return;

        try {
            // Exact Dimensions (mm)
            const totalW = 57.3;
            const totalH = 89.3;
            
            const doc = new jsPDF('p', 'mm', [totalW, totalH]);
            const blueColor = [45, 66, 154]; // Matched to logo blue
            const startX = 0;

            for (let i = 0; i < items.length; i++) {
                const inquiry = items[i];
                if (i > 0) doc.addPage([totalW, totalH]);

                let yPos = 0;

                // 1. Blue Header (10mm)
                doc.setFillColor(...blueColor);
                doc.rect(startX, yPos, totalW, 10, 'F');
                
                // Centering Logo + School Name
                const schoolNameText = 'KALLADI ABDU HAJI HSS';
                doc.setFontSize(7.5);
                doc.setFont(undefined, 'bold');
                const textW = doc.getTextWidth(schoolNameText);
                const logoW = 6;
                const headerGap = 2;
                const totalHeaderContentW = logoW + headerGap + textW;
                const headerStartX = startX + (totalW - totalHeaderContentW) / 2;

                // School Logo
                try {
                    doc.addImage(schoolLogo, 'PNG', headerStartX, yPos + 2, logoW, logoW);
                } catch (e) {
                    doc.setFillColor(255, 255, 255);
                    doc.circle(headerStartX + logoW/2, yPos + 5, logoW/2, 'F');
                }
                
                // School Name
                doc.setTextColor(255, 255, 255);
                doc.text(schoolNameText, headerStartX + logoW + headerGap, yPos + 6);
                yPos += 10;

                // 2. Main Design Area (White BG)
                const footerH = 9; 
                const mainAreaH = totalH - 10 - footerH;
                doc.setFillColor(255, 255, 255);
                doc.rect(startX, yPos, totalW, mainAreaH, 'F');
                
                // Photo Area (24x28mm)
                const photoW = 24;
                const photoH = 28;
                const photoX = startX + (totalW - photoW) / 2;
                const photoY = yPos + 2.5;
                const cornerRadius = 1.5;
                
                if (inquiry.profile_picture_url) {
                    try {
                        const img = await loadImage(`${imageBaseUrl}${inquiry.profile_picture_url}`);
                        const roundedImgData = getRoundedImage(img, photoW, photoH, cornerRadius);
                        doc.addImage(roundedImgData, 'PNG', photoX, photoY, photoW, photoH);
                    } catch (e) {
                        doc.setDrawColor(230);
                        doc.roundedRect(photoX, photoY, photoW, photoH, cornerRadius, cornerRadius, 'D');
                        doc.setFontSize(5);
                        doc.setTextColor(150);
                        doc.text('PHOTO', photoX + photoW/2, photoY + photoH/2, { align: 'center' });
                    }
                } else {
                    doc.setDrawColor(230);
                    doc.roundedRect(photoX, photoY, photoW, photoH, cornerRadius, cornerRadius, 'D');
                    doc.setFontSize(5);
                    doc.setTextColor(150);
                    doc.text('NO PHOTO', photoX + photoW/2, photoY + photoH/2, { align: 'center' });
                }

                // Add a light border on top of the clipped photo for polish
                doc.setDrawColor(230);
                doc.setLineWidth(0.3);
                doc.roundedRect(photoX, photoY, photoW, photoH, cornerRadius, cornerRadius, 'D');

                // Rotated Academic Year
                doc.setTextColor(100);
                doc.setFontSize(7);
                doc.setFont(undefined, 'bold');
                doc.text(currentYear?.year_name || '2026-27', startX + totalW - 4, photoY + photoH/2, { angle: -90, align: 'center' });

                // Student Name (Under photo)
                let nameY = photoY + photoH + 5.5;
                doc.setTextColor(...blueColor);
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                const name = (inquiry.student_name || '').toUpperCase();
                let nameFontSize = 10;
                doc.setFontSize(nameFontSize);
                while (doc.getTextWidth(name) > totalW - 10 && nameFontSize > 6.5) {
                    nameFontSize -= 0.5;
                    doc.setFontSize(nameFontSize);
                }
                doc.text(name, startX + totalW / 2, nameY, { align: 'center' });

                // Student Details Rows
                doc.setTextColor(50);
                doc.setFontSize(6.5);
                doc.setFont(undefined, 'normal');
                let detailY = nameY + 4.5;
                const lineH = 3.5;
                const labelX = startX + 5;
                const colonX = startX + 22;
                const valueX = startX + 24;

                const details = [
                    ['Admission No', inquiry.admission_no || 'N/A'],
                    ['Father Name', inquiry.parent_name || 'N/A'],
                    ['Date of Birth', inquiry.dob ? dayjs(inquiry.dob).format('DD-MM-YYYY') : 'N/A'],
                    ['Class', classes.find(c => c.id === inquiry.class_id)?.name || 'N/A'],
                ];

                details.forEach(([label, value]) => {
                    doc.setFont(undefined, 'bold');
                    doc.text(label, labelX, detailY);
                    doc.text(':', colonX, detailY);
                    doc.setFont(undefined, 'normal');
                    doc.text(String(value), valueX, detailY);
                    detailY += lineH;
                });

                // Address (Multiline)
                doc.setFont(undefined, 'bold');
                doc.text('Address', labelX, detailY);
                doc.text(':', colonX, detailY);
                doc.setFont(undefined, 'normal');
                const addr = inquiry.address || 'N/A';
                const splitAddr = doc.splitTextToSize(addr, totalW - 29);
                doc.text(splitAddr, valueX, detailY);
                const addrLines = Array.isArray(splitAddr) ? splitAddr.length : 1;
                detailY += Math.max(1.8, addrLines) * lineH;

                // Phone
                doc.setFont(undefined, 'bold');
                doc.text('Phone', labelX, detailY);
                doc.text(':', colonX, detailY);
                doc.setFont(undefined, 'normal');
                doc.text(String(inquiry.phone_number || 'N/A'), valueX, detailY);

                // 3. Blue Footer (Multiline)
                const footerY = totalH - footerH;
                doc.setFillColor(...blueColor);
                doc.rect(startX, footerY, totalW, footerH, 'F');
                
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(5);
                doc.setFont(undefined, 'normal');
                doc.text('KAHHSS, KOTTOPADAM, MANNARKKAD, INDIA, 678583', startX + totalW / 2, footerY + 3.5, { align: 'center' });
                doc.text('Phone: 04924 262 033, Mail: kahhss9128@gmail.com', startX + totalW / 2, footerY + 6.5, { align: 'center' });
            }

            const fileName = items.length === 1 ? `${items[0].student_name}_ID_Card.pdf` : `Bulk_ID_Cards.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error(error);
            message.error('Failed to generate ID info PDF.');
        }
    };

    const handlePrintFull = async (inquiryOrList) => {
        const items = Array.isArray(inquiryOrList) ? inquiryOrList : [inquiryOrList];
        if (items.length === 0) return;

        try {
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();

            for (let i = 0; i < items.length; i++) {
                const inquiry = items[i];
                if (i > 0) doc.addPage();

                let yPos = 15;
                doc.setFontSize(18);
                doc.setFont(undefined, 'bold');
                doc.text('ADMISSION APPLICATION DETAILS', pageWidth / 2, yPos, { align: 'center' });
                yPos += 10;

                if (inquiry.profile_picture_url) {
                    try {
                        const img = await loadImage(`${imageBaseUrl}${inquiry.profile_picture_url}`);
                        doc.addImage(img, 'JPEG', pageWidth - 45, yPos, 30, 40);
                    } catch (e) { }
                }

                doc.setFontSize(12);
                const data = [
                    ['Student Name', inquiry.student_name],
                    ['Admission No', inquiry.admission_no || 'N/A'],
                    ['Class', classes.find(c => c.id === inquiry.class_id)?.name || 'N/A'],
                    ['Date of Birth', inquiry.dob ? dayjs(inquiry.dob).format('DD MMMM YYYY') : 'N/A'],
                    ['Gender', inquiry.gender || 'N/A'],
                    ['Phone Number', inquiry.phone_number || 'N/A'],
                    ['Address', inquiry.address || 'N/A'],
                    ['Father Name', inquiry.parent_name],
                    ['Mother Name', inquiry.mother_name || 'N/A'],
                    ['Guardian Name', inquiry.guardian_name || 'N/A'],
                    ['Guardian Phone', inquiry.guardian_phone_number || 'N/A'],
                    ['Religion & Caste', inquiry.religion_caste || 'N/A'],
                    ['Aadhar No', inquiry.aadhar_no || 'N/A'],
                    ['SSLC Reg No', inquiry.sslc_reg_no || 'N/A'],
                    ['SSLC School', inquiry.sslc_school || 'N/A']
                ];

                autoTable(doc, {
                    startY: yPos,
                    margin: { right: 50 },
                    body: data,
                    theme: 'striped',
                    styles: { fontSize: 10 }
                });

                yPos = doc.lastAutoTable.finalY + 10;
                doc.setFont(undefined, 'bold');
                doc.text('SSLC Grades', 15, yPos);
                yPos += 5;

                const grades = [
                    ['Physics', inquiry.sslc_physics || 'N/A', 'Chemistry', inquiry.sslc_chemistry || 'N/A'],
                    ['Maths', inquiry.sslc_maths || 'N/A', 'Biology', inquiry.sslc_biology || 'N/A'],
                    ['Social Studies', inquiry.sslc_social_studies || 'N/A', 'English', inquiry.sslc_english || 'N/A']
                ];

                autoTable(doc, {
                    startY: yPos,
                    body: grades,
                    theme: 'grid',
                    styles: { fontSize: 10 }
                });
            }

            const fileName = items.length === 1 ? `${items[0].student_name}_Application.pdf` : `Bulk_Applications.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error(error);
            message.error('Failed to generate full application PDF.');
        }
    };

    const handleViewClick = async (inquiryId) => {
        try {
            const fullInquiryData = await getInquiryById(inquiryId);
            setSelectedInquiry(fullInquiryData);
            setIsViewDrawerVisible(true);
        } catch (error) {
            message.error('Failed to fetch inquiry details.');
        }
    };
    const handleDrawerClose = () => { setIsViewDrawerVisible(false); setSelectedInquiry(null); };

    const handleModalOk = () => {
        form.validateFields().then(async values => {
            if (!selectedInquiry) return;
            try {
                await acceptInquiry(selectedInquiry.id, values);
                message.success('Student admitted successfully!');
                handleModalCancel();
                fetchData();
            } catch (error) { message.error(error.response?.data?.error || 'Failed to admit student.'); }
        });
    };

    const handleReject = async (id) => {
        try {
            await rejectInquiry(id);
            message.success('Application has been rejected.');
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to reject application.');
        }
    };
    const renderYesNo = (value) => value ? 'Yes' : 'No';

    const columns = [
        {
            title: 'Photo',
            key: 'photo',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Avatar src={getAvatarSrc(record)} icon={<UserOutlined />} />
            )
        },
        { title: 'Submitted On', dataIndex: 'submission_date', key: 'submission_date' },
        { title: 'Student Name', dataIndex: 'student_name', key: 'student_name' },
        { title: 'Parent Name', dataIndex: 'parent_name', key: 'parent_name' },
        { title: 'Guardian Phone', dataIndex: 'guardian_phone_number', key: 'guardian_phone_number' },
        {
            title: 'Actions', key: 'action', align: 'center', fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button icon={<EyeOutlined />} onClick={() => handleViewClick(record.id)} />
                    </Tooltip>
                    <Tooltip title="Print ID Info">
                        <Button icon={<PrinterOutlined />} onClick={() => handlePrintIDCard(record)} />
                    </Tooltip>
                    <Tooltip title="Print Full Application">
                        <Button icon={<DownloadOutlined />} onClick={() => handlePrintFull(record)} />
                    </Tooltip>
                    {status === 'pending' && (
                        <>
                            <Button type="default" icon={<CheckOutlined />} onClick={() => handleAcceptClick(record)}>Accept</Button>
                            <Popconfirm okType='default' title="Reject this application?" onConfirm={() => handleReject(record.id)}>
                                <Button danger icon={<CloseOutlined />}>Reject</Button>
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;
    const selectedRecords = inquiries.filter(i => selectedRowKeys.includes(i.id));

    return (
        <div style={{ padding: isMobile ? '12px' : '24px', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={2} style={{ fontWeight: 800, margin: 0 }}>{status.charAt(0).toUpperCase() + status.slice(1)} Admission Inquiries</Title>
                    <Text type="secondary" style={{ fontSize: 15 }}>Review and process public admission applications for the current term.</Text>
                </div>
                <Space>
                    {hasSelected && (
                        <>
                            <Text strong>{selectedRowKeys.length} items selected</Text>
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={() => handlePrintIDCard(selectedRecords)}
                                type="primary"
                            >
                                Print ID Info
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={() => handlePrintFull(selectedRecords)}
                            >
                                Print Full Applications
                            </Button>
                        </>
                    )}
                </Space>
            </div>
            
            <Table
                rowSelection={rowSelection}
                dataSource={inquiries}
                columns={columns}
                loading={loading}
                bordered
                style={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    onShowSizeChange: (current, size) => setPageSize(size),
                    onChange: (page, size) => setPageSize(size),
                    responsive: true
                }}
                scroll={{ x: 'max-content' }}
            />

            {selectedInquiry && (
                <Modal
                    okType='default'
                    title={`Admit Student: ${selectedInquiry.student_name}`}
                    open={isAcceptModalVisible}
                    onOk={handleModalOk}
                    onCancel={handleModalCancel}
                    okText="Admit Student"
                >
                    <Form form={form} layout="vertical">
                        <Form.Item name="classId" label="Assign to Class" rules={[{ required: true }]}>
                            <Select placeholder="Select a class for this student">
                                {classes.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item name="admissionNo" label="Assign Admission Number" rules={[{ required: true }]}>
                            <Input placeholder="Enter a unique admission number" />
                        </Form.Item>
                    </Form>
                </Modal>
            )}

            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar src={getAvatarSrc(selectedInquiry)} icon={<UserOutlined />} />
                        <div>
                            <Text strong style={{ fontSize: 16, display: 'block' }}>{selectedInquiry?.student_name}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>ID: {selectedInquiry?.id}</Text>
                        </div>
                    </div>
                }
                width={720}
                onClose={handleDrawerClose}
                open={isViewDrawerVisible}
                bodyStyle={{ padding: '24px', backgroundColor: '#FAFAFA' }}
            >
                {selectedInquiry ? (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <Card title="Student Profile" size="small" bordered={false} style={{ borderRadius: '8px' }}>
                            <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                                <Descriptions.Item label="Name" span={2}>{selectedInquiry.student_name}</Descriptions.Item>
                                <Descriptions.Item label="Admission Number">{selectedInquiry.admission_no || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Date of Birth">{selectedInquiry.dob ? dayjs(selectedInquiry.dob).format('DD MMM, YYYY') : 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Gender" style={{ textTransform: 'capitalize' }}>{selectedInquiry.gender || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Phone Number">{selectedInquiry.phone_number || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Religion & Caste">{selectedInquiry.religion_caste || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Blood Group">{selectedInquiry.blood_group || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Aadhar No" span={2}>{selectedInquiry.aadhar_no || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Address" span={2}>{selectedInquiry.address || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                        </Card>

                        <Card title="Academic History" size="small" bordered={false} style={{ borderRadius: '8px' }}>
                            <Descriptions bordered column={2} size="small">
                                <Descriptions.Item label="SSLC Reg. No">{selectedInquiry.sslc_reg_no || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="SSLC School">{selectedInquiry.sslc_school || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Maths">{selectedInquiry.sslc_maths || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Physics">{selectedInquiry.sslc_physics || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Chemistry">{selectedInquiry.sslc_chemistry || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Biology">{selectedInquiry.sslc_biology || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Social Studies">{selectedInquiry.sslc_social_studies || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="English">{selectedInquiry.sslc_english || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                        </Card>

                        <Card title="Family & Contact" size="small" bordered={false} style={{ borderRadius: '8px' }}>
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="Father's Name">{selectedInquiry.parent_name}</Descriptions.Item>
                                <Descriptions.Item label="Mother's Name">{selectedInquiry.mother_name || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Guardian Name">{selectedInquiry.guardian_name || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Guardian Phone">{selectedInquiry.guardian_phone_number || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                        </Card>

                        <Card title="Additional Information" size="small" bordered={false} style={{ borderRadius: '8px' }}>
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="Ration Card">{selectedInquiry.ration_card_type || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Scholarship Received">{renderYesNo(selectedInquiry.scholarship_received)}</Descriptions.Item>
                                <Descriptions.Item label="Extra-Curricular (Arts)">{renderYesNo(selectedInquiry.participated_in_arts)}</Descriptions.Item>
                                <Descriptions.Item label="Extra-Curricular (Sports)">{renderYesNo(selectedInquiry.participated_in_sports)}</Descriptions.Item>
                                <Descriptions.Item label="Health Issues">{selectedInquiry.chronic_illnesses || 'None Reported'}</Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Space>
                ) : <div style={{ textAlign: 'center', padding: '40px' }}><Spin tip="Loading application data..." /></div>}
            </Drawer>
        </div>
    );
};

export default InquiryListPage;
